import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, getFirestore } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { loadHubFeatureFlags, getHubFeatureFlags, isFeatureEnabled, loadFeatureTogglesFromSettings } from '../utils/featureFlags';
import { getFirebaseDb } from '../config/firebase';

// ─── Hub Firebase connection ───────────────────────────────────────────────────
const HUB_FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_HUB_FIREBASE_API_KEY || 'AIzaSyD3Nn0PRquje1FPdTvQ9-5jULiaN02JEZs',
  authDomain: process.env.REACT_APP_HUB_FIREBASE_AUTH_DOMAIN || 'jupiter-to-plutoo.firebaseapp.com',
  projectId: process.env.REACT_APP_HUB_FIREBASE_PROJECT_ID || 'jupiter-to-plutoo',
  storageBucket: process.env.REACT_APP_HUB_FIREBASE_STORAGE_BUCKET || 'jupiter-to-plutoo.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_HUB_FIREBASE_MESSAGING_SENDER_ID || '250141547970',
  appId: process.env.REACT_APP_HUB_FIREBASE_APP_ID || '1:250141547970:web:a7bdd7e05f21089f6b0a5b',
};

const HUB_APP_NAME = 'plutoo-hub-readonly';

const getHubDb = () => {
  const existing = getApps().find((a) => a.name === HUB_APP_NAME);
  const hubApp = existing || initializeApp(HUB_FIREBASE_CONFIG, HUB_APP_NAME);
  return getFirestore(hubApp);
};

const FeatureFlagsContext = createContext({
  hubFlags: null,
  localFlags: null,
  isEnabled: isFeatureEnabled,
  loading: true,
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export const FeatureFlagsProvider = ({ children }) => {
  const [hubFlags, setHubFlags] = useState(() => getHubFeatureFlags());
  const [localFlags, setLocalFlags] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const companyName = process.env.REACT_APP_COMPANY_NAME || 'demo-plutoo';

    // Load both flag sources eagerly
    Promise.all([
      loadHubFeatureFlags().catch(() => null),
      loadFeatureTogglesFromSettings().catch(() => null),
    ]).then(([hub, local]) => {
      if (hub) setHubFlags(hub);
      if (local) setLocalFlags(local);
      setLoading(false);
    });

    // Subscribe to Hub Firestore for live changes
    let unsubHub = () => {};
    try {
      const hubDb = getHubDb();
      unsubHub = onSnapshot(
        doc(hubDb, 'companyFeatures', companyName),
        (snap) => {
          if (snap.exists()) {
            const features = snap.data().features || null;
            setHubFlags(features);
            setLoading(false);
          }
        },
        () => setLoading(false)
      );
    } catch {
      setLoading(false);
    }

    // Subscribe to local Firestore settings
    let unsubLocal = () => {};
    try {
      unsubLocal = onSnapshot(
        doc(getFirebaseDb(), 'settings', 'appSettings'),
        (snap) => {
          if (snap.exists() && snap.data().features) {
            setLocalFlags(snap.data().features);
          }
        },
        () => {}
      );
    } catch {
      // ignore
    }

    return () => {
      unsubHub();
      unsubLocal();
    };
  }, []);

  // Hub flags take priority over local Firestore flags
  const isEnabled = (featureName) => {
    if (hubFlags && Object.prototype.hasOwnProperty.call(hubFlags, featureName)) {
      return hubFlags[featureName];
    }
    if (localFlags && Object.prototype.hasOwnProperty.call(localFlags, featureName)) {
      return localFlags[featureName];
    }
    return isFeatureEnabled(featureName);
  };

  return (
    <FeatureFlagsContext.Provider value={{ hubFlags, localFlags, isEnabled, loading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
